import styled, { keyframes } from 'styled-components';

import { Rarity } from '__generated__/globalTypes';
import { Horizontal } from 'atoms/layout/flex';

import christmas_limited from './assets/christmas/limited.jpg';
import christmas_rare from './assets/christmas/rare.jpg';
import christmas_super_rare from './assets/christmas/super_rare.jpg';
import christmas_unique from './assets/christmas/unique.jpg';
import common from './assets/common.jpg';
import custom_series from './assets/custom_series.jpg';
import bgUrl from './assets/halloween/bg.png';
import bottomLeftUrl from './assets/halloween/bottom-left.png';
import bottomRightUrl from './assets/halloween/bottom-right.png';
import topLeftUrl from './assets/halloween/top-left.png';
import topRightUrl from './assets/halloween/top-right.png';
import limited from './assets/limited.jpg';
import playoffs_limited from './assets/playoffs-24/limited.jpg';
import playoffs_rare from './assets/playoffs-24/rare.jpg';
import playoffs_super_rare from './assets/playoffs-24/super_rare.jpg';
import playoffs_unique from './assets/playoffs-24/unique.jpg';
import rare from './assets/rare.jpg';
import super_rare from './assets/super_rare.jpg';
import unique from './assets/unique.jpg';

const movingBackground = keyframes`
  from {
    background-position: 0 0;
  }
  to {
    background-position: 100% 0;
  }
`;

const BackgroundContainer = styled(Horizontal)`
  position: absolute;
  inset: 0;
  display: flex;
  animation: ${movingBackground} var(--duration) linear infinite;
`;

type Assets = Record<Rarity, string>;

const christmasAssets: Assets = {
  [Rarity.common]: `url(${common})`,
  [Rarity.custom_series]: `url(${custom_series})`,
  [Rarity.limited]: `url(${christmas_limited})`,
  [Rarity.rare]: `url(${christmas_rare})`,
  [Rarity.super_rare]: `url(${christmas_super_rare})`,
  [Rarity.unique]: `url(${christmas_unique})`,
};

const classic_assets: Assets = {
  [Rarity.common]: `url(${common})`,
  [Rarity.custom_series]: `url(${custom_series})`,
  [Rarity.limited]: `url(${limited})`,
  [Rarity.rare]: `url(${rare})`,
  [Rarity.super_rare]: `url(${super_rare})`,
  [Rarity.unique]: `url(${unique})`,
};

const playoffs_24_assets: Assets = {
  [Rarity.common]: `url(${common})`,
  [Rarity.custom_series]: `url(${custom_series})`,
  [Rarity.limited]: `url(${playoffs_limited})`,
  [Rarity.rare]: `url(${playoffs_rare})`,
  [Rarity.super_rare]: `url(${playoffs_super_rare})`,
  [Rarity.unique]: `url(${playoffs_unique})`,
};

const halloween_style = `
      url("${bottomLeftUrl}") left bottom / min(40%, 270px) no-repeat,
      url("${bottomRightUrl}") right bottom / min(40%, 270px) no-repeat,
      url("${topLeftUrl}") left top / min(40%, 270px) no-repeat,
      url("${topRightUrl}") right top / min(40%, 270px) no-repeat,
      url("${bgUrl}") center / cover no-repeat
      `;
const halloween: Assets = {
  [Rarity.common]: halloween_style,
  [Rarity.custom_series]: halloween_style,
  [Rarity.limited]: halloween_style,
  [Rarity.rare]: halloween_style,
  [Rarity.super_rare]: halloween_style,
  [Rarity.unique]: halloween_style,
};

type Background = {
  assets: Assets;
  duration: string;
  style?: React.CSSProperties;
};

const PLAYOFFS_24_VALUES = ['Postseason™', 'ace'];

const getBackgroundFromEditionName = (
  editionName?: string | null
): Background => {
  if (editionName) {
    if (editionName?.startsWith('neon_christmas')) {
      return {
        assets: christmasAssets,
        duration: '50s',
        style: {
          width: '900px',
        },
      };
    }
    if (PLAYOFFS_24_VALUES.includes(editionName)) {
      return {
        assets: playoffs_24_assets,
        duration: '25s',
      };
    }
    if (['halloween', 'cursed'].includes(editionName)) {
      return {
        assets: halloween,
        duration: '25s',
        style: {
          animation: 'none',
        },
      };
    }
  }
  return { assets: classic_assets, duration: '25s' };
};

type Props = {
  rarity: Rarity;
  editionName?: string | null;
};

export const ScarcityBackground = ({ rarity, editionName }: Props) => {
  const { assets, duration, style } = getBackgroundFromEditionName(editionName);

  return (
    <BackgroundContainer
      style={
        {
          background: assets[rarity],
          '--duration': duration,
          ...style,
        } as React.CSSProperties
      }
    />
  );
};
